// PlutusRealtime — speech-to-speech voice agent (OpenAI Realtime API over WebRTC).
// Open-mic + barge-in + live captions + audio-reactive orb + tool calling, grounded
// in the trader's live context (risk profile, latest analysis, prices, TDI).
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './SafeIcon';
import { useAuth } from '@/contexts/AuthContext';
import useRiskProfile from '@/hooks/useRiskProfile';
import {
  fetchAllPrices,
  fetchTodayStats,
  fetchJournalContext,
  createSessionMemory,
  buildStructuredContext,
  formatContextForAI,
} from '@/services/plutus/contextBuilder';
import { RealtimeChat } from '@/utils/RealtimeAudio';
import { PLUTUS_TOOLS, executePlutusTool, buildPlutusInstructions } from '@/services/plutus/realtimeTools';

const VOICES = [
  { id: 'cedar', label: 'Cedar' },
  { id: 'marin', label: 'Marin' },
  { id: 'shimmer', label: 'Calm' },
];

// WebRTC data channels have a size cap — downscale/compress the chart before sending it.
const downscaleImage = (dataUrl, maxW = 1024, quality = 0.6) =>
  new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    } catch {
      resolve(dataUrl);
    }
  });

const PlutusRealtime = ({ currentPrice, selectedMarket, chartImage, onClose }) => {
  const { user } = useAuth();
  const { profile: riskProfile, riskAmount, maxDailyLoss, loading: riskProfileLoading } = useRiskProfile();

  const [phase, setPhase] = useState('idle'); // idle | connecting | live | error
  const [speaking, setSpeaking] = useState(false); // AI speaking
  const [listening, setListening] = useState(false); // user speaking (VAD)
  const [statusText, setStatusText] = useState('Tap to start');
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userPartial, setUserPartial] = useState('');
  const [aiPartial, setAiPartial] = useState('');
  const [level, setLevel] = useState(0); // 0..1 audio amplitude for the orb
  const [voice, setVoice] = useState('cedar');
  const [textInput, setTextInput] = useState('');

  const chatRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const userAnalyserRef = useRef(null);
  const aiAnalyserRef = useRef(null);
  const ctxBlockRef = useRef('');
  const sessionMemoryRef = useRef(createSessionMemory());
  const endRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, userPartial, aiPartial]);

  // Build the live context block from the trader's real data.
  const buildContextBlock = useCallback(async () => {
    const prices = await fetchAllPrices();
    let todayStats = null;
    let journal = null;
    if (user?.id) {
      [todayStats, journal] = await Promise.all([
        fetchTodayStats(user.id, riskAmount),
        fetchJournalContext(user.id),
      ]);
    }
    const structured = buildStructuredContext(
      selectedMarket,
      currentPrice ?? null,
      prices,
      riskProfile,
      riskAmount,
      maxDailyLoss,
      todayStats,
      journal,
      sessionMemoryRef.current,
      null,
      riskProfileLoading
    );
    const block = formatContextForAI(structured);
    ctxBlockRef.current = block;
    return block;
  }, [user, riskProfile, riskAmount, maxDailyLoss, riskProfileLoading, selectedMarket, currentPrice]);

  // Audio-reactive orb: read amplitude from mic + AI streams each frame.
  const startVisualizer = useCallback(() => {
    const tick = () => {
      const read = (analyser) => {
        if (!analyser) return 0;
        const buf = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        return Math.min(1, Math.sqrt(sum / buf.length) * 3.2);
      };
      const u = read(userAnalyserRef.current);
      const a = read(aiAnalyserRef.current);
      setLevel(Math.max(u, a));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const attachAnalyser = useCallback((stream, ref) => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ac = audioCtxRef.current;
      if (ac.state === 'suspended') ac.resume();
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      ref.current = analyser;
    } catch (e) {
      console.warn('Analyser attach failed:', e);
    }
  }, []);

  const handleFunctionCall = useCallback(async (name, args, callId) => {
    setStatusText('Looking that up…');
    const result = await executePlutusTool(name, args, { userId: user?.id || null, riskAmount });
    chatRef.current?.sendFunctionResult(callId, result);
  }, [user, riskAmount]);

  const start = useCallback(async () => {
    setError(null);
    setPhase('connecting');
    setStatusText('Connecting…');
    try {
      const block = await buildContextBlock();
      const instructions = buildPlutusInstructions(block);

      const chat = new RealtimeChat({
        onMessage: () => {},
        onConnected: () => {
          setPhase('live');
          setStatusText('Listening…');
          // Give Plutus eyes: push the current chart silently into context on connect.
          if (chartImage) {
            downscaleImage(chartImage)
              .then((small) => chatRef.current?.sendImage(small))
              .catch(() => {});
          }
        },
        onDisconnected: () => {
          setPhase('idle');
          setStatusText('Disconnected');
        },
        onError: (err) => {
          setError(err.message || 'Voice connection error');
          setPhase('error');
          setStatusText('Error');
        },
        onSpeakingChange: (isSpeaking) => {
          setSpeaking(isSpeaking);
          setStatusText(isSpeaking ? 'Plutus is speaking…' : 'Listening…');
        },
        onUserTranscriptDelta: (delta) => {
          setListening(true);
          setUserPartial((p) => p + delta);
        },
        onTranscript: (text, isFinal) => {
          if (isFinal && text) {
            setMessages((m) => [...m, { role: 'user', text }]);
            setUserPartial('');
            setListening(false);
          }
        },
        onAiTranscript: (text, isFinal) => {
          if (isFinal) {
            if (text) setMessages((m) => [...m, { role: 'assistant', text }]);
            setAiPartial('');
          } else {
            setAiPartial((p) => p + text);
          }
        },
        onFunctionCall: handleFunctionCall,
        onRemoteStream: (stream) => attachAnalyser(stream, aiAnalyserRef),
      });

      chatRef.current = chat;
      await chat.connect(instructions, voice, PLUTUS_TOOLS);

      // Mic stream for the visualizer
      const local = chat.getLocalStream();
      if (local) attachAnalyser(local, userAnalyserRef);
      startVisualizer();
    } catch (err) {
      console.error('Plutus realtime start failed:', err);
      setError(err?.message || 'Could not start voice. Check mic permissions.');
      setPhase('error');
      setStatusText('Error');
    }
  }, [buildContextBlock, voice, handleFunctionCall, attachAnalyser, startVisualizer, chartImage]);

  // Share the current chart with Plutus and ask him to read it.
  const showChart = useCallback(async () => {
    if (!chartImage || !chatRef.current) return;
    setStatusText('Looking at your chart…');
    const small = await downscaleImage(chartImage);
    setMessages((m) => [...m, { role: 'user', text: '📊 Shared my chart' }]);
    chatRef.current.sendImage(
      small,
      "Here's the chart I'm looking at right now. What do you see, and would you take this setup?",
      true
    );
  }, [chartImage]);

  const stop = useCallback(() => {
    chatRef.current?.disconnect();
    chatRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    userAnalyserRef.current = null;
    aiAnalyserRef.current = null;
    setPhase('idle');
    setSpeaking(false);
    setListening(false);
    setLevel(0);
  }, []);

  // Keep the model's context fresh when the asset/price changes mid-session.
  useEffect(() => {
    if (phase !== 'live') return;
    const t = setTimeout(async () => {
      const block = await buildContextBlock();
      chatRef.current?.updateSession({ type: 'realtime', instructions: buildPlutusInstructions(block) });
    }, 800);
    return () => clearTimeout(t);
  }, [selectedMarket, currentPrice, phase, buildContextBlock]);

  // Cleanup on unmount
  useEffect(() => () => stop(), [stop]);

  const sendText = () => {
    const t = textInput.trim();
    if (!t || !chatRef.current) return;
    setMessages((m) => [...m, { role: 'user', text: t }]);
    chatRef.current.sendTextMessage(t);
    setTextInput('');
  };

  const handleClose = () => { stop(); onClose?.(); };

  const orbScale = 1 + level * 0.5;
  const orbColor = speaking ? 'from-purple-500 to-fuchsia-500' : listening ? 'from-cyan-400 to-blue-500' : 'from-purple-600/40 to-cyan-600/40';

  return (
    <div className="w-full h-full flex flex-col bg-gray-900 rounded-2xl border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
            <SafeIcon icon={FiIcons.FiShield} className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-white font-bold leading-tight">Plutus: Institutional Risk Officer <span className="text-[10px] text-cyan-400 align-top">BETA</span></h3>
            <p className="text-xs text-gray-500">Realtime voice · gpt-realtime</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            disabled={phase === 'live' || phase === 'connecting'}
            className="bg-gray-800 border border-white/10 text-gray-300 text-xs rounded-lg px-2 py-1.5 disabled:opacity-50"
          >
            {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
          {phase === 'live' ? (
            <button onClick={stop} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20">
              <SafeIcon icon={FiIcons.FiPhoneOff} className="w-4 h-4" /> End
            </button>
          ) : null}
          <button onClick={handleClose} className="w-8 h-8 rounded-lg bg-gray-800 border border-white/10 text-gray-400 hover:text-white flex items-center justify-center">
            <SafeIcon icon={FiIcons.FiX} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-5 py-2 bg-gray-900/60 border-b border-white/5 text-xs">
        <div className="flex items-center gap-2 text-gray-400">
          <span className={`w-2 h-2 rounded-full ${phase === 'live' ? 'bg-emerald-500 animate-pulse' : 'bg-gray-600'}`} />
          {statusText}
        </div>
        <div className="flex items-center gap-2 text-gray-500">
          <span className="text-cyan-400/70">CTX</span>
          {selectedMarket} {currentPrice ? `@ $${Number(currentPrice).toLocaleString()}` : ''}
        </div>
      </div>

      {/* Orb */}
      <div className="flex flex-col items-center justify-center py-8 bg-gradient-to-b from-purple-950/20 to-transparent">
        <button
          onClick={phase === 'idle' || phase === 'error' ? start : undefined}
          disabled={phase === 'connecting'}
          className="relative outline-none"
        >
          <motion.div
            animate={{ scale: phase === 'live' ? orbScale : 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            className={`w-28 h-28 rounded-full bg-gradient-to-br ${orbColor} flex items-center justify-center shadow-2xl`}
            style={{ boxShadow: `0 0 ${20 + level * 60}px rgba(139,92,246,${0.3 + level * 0.5})` }}
          >
            <SafeIcon
              icon={phase === 'connecting' ? FiIcons.FiLoader : speaking ? FiIcons.FiVolume2 : FiIcons.FiMic}
              className={`w-9 h-9 text-white ${phase === 'connecting' ? 'animate-spin' : ''}`}
            />
          </motion.div>
        </button>
        <p className="mt-4 text-sm text-gray-300 font-medium">
          {phase === 'idle' || phase === 'error' ? 'Tap to start talking' : phase === 'connecting' ? 'Connecting…' : speaking ? 'Plutus is speaking' : listening ? 'Listening…' : 'Speak anytime'}
        </p>
        {phase === 'live' && <p className="text-[11px] text-gray-500 mt-1">Open mic · just talk · interrupt anytime</p>}
        {phase === 'live' && chartImage && (
          <button
            onClick={showChart}
            className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-300 text-sm hover:from-purple-600/30 hover:to-cyan-600/30 transition-all"
          >
            <SafeIcon icon={FiIcons.FiEye} className="w-4 h-4" /> Show Plutus my chart
          </button>
        )}
        {error && <p className="text-xs text-red-400 mt-2 max-w-sm text-center">{error}</p>}
      </div>

      {/* Transcript */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {messages.length === 0 && phase !== 'live' && (
          <div className="text-center text-gray-600 text-sm mt-6">
            Plutus sees your live risk profile, latest analysis, prices, TDI — and your actual chart.
            Tap "Show Plutus my chart", then ask "what do you see — would you take it?", "how much can
            I risk here?", or "what's my TDI?"
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${m.role === 'user' ? 'bg-cyan-500/15 border border-cyan-500/20 text-cyan-50' : 'bg-gray-800/70 border border-white/10 text-gray-200'}`}>
              {m.role === 'assistant' && <span className="block text-[10px] text-purple-400 mb-0.5">Plutus</span>}
              {m.text}
            </div>
          </div>
        ))}
        {userPartial && (
          <div className="flex justify-end">
            <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm bg-cyan-500/10 border border-cyan-500/10 text-cyan-100/70 italic">{userPartial}…</div>
          </div>
        )}
        {aiPartial && (
          <div className="flex justify-start">
            <div className="max-w-[80%] px-4 py-2.5 rounded-2xl text-sm bg-gray-800/50 border border-white/5 text-gray-300/80">
              <span className="block text-[10px] text-purple-400 mb-0.5">Plutus</span>{aiPartial}…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Text fallback */}
      <div className="px-4 py-3 border-t border-white/10 flex items-center gap-2">
        <input
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendText()}
          disabled={phase !== 'live'}
          placeholder={phase === 'live' ? 'Type or just talk…' : 'Start voice to chat'}
          className="flex-1 bg-gray-800/60 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/40 disabled:opacity-50"
        />
        <button onClick={sendText} disabled={phase !== 'live' || !textInput.trim()} className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 flex items-center justify-center disabled:opacity-40">
          <SafeIcon icon={FiIcons.FiSend} className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
};

export default PlutusRealtime;
